/* eslint-disable react-hooks/exhaustive-deps */
import { useAppContext } from "App/AppContext"
import { useAuth } from "Auth"
import { URLS } from "Common/urls"
import { useSnackbar } from "Components/Snackbar"
import React, { createContext, useState, useEffect, useContext } from "react"
import RestService from "Services/RestService"
import { currentDateTimeString } from "Utils/date"

// Create the context
const CartContext = createContext()

// Provider component
const CartProvider = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth()
    const { selectedSupplier, selectedBranch, showSelectBranch: branchForSessionOpen, showSelectSupplier: supplierForSessionOpen } = useAppContext()
    const { showSuccess } = useSnackbar()
    const [cart, setCart] = useState([])
    const [loading, setLoading] = useState(false)

    // Fetch the cart from the API
    const fetchCart = async () => {
        setLoading(true)
        RestService.get(`${URLS.CART_URL}?clientCode=${selectedSupplier.clientcode}&custCode=${selectedBranch.custcode}`)
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setCart(data)
                }
            }).catch(error => {
                console.error("Failed to fetch cart", error)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Add an item to the cart
    const addItemToCart = async (item) => {
        setLoading(true)
        const body = {
            clientCode: selectedSupplier.clientcode,
            custCode: selectedBranch.custcode,
            prdCode: item.prdcode,
            qty: item.qty,
            orderDate: currentDateTimeString(),
            remarks: item.remarks,
        }
        return RestService.put(URLS.CART_URL, body)
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setCart((prevCart) => [...prevCart, data])
                    showSuccess("Added Item to Cart")
                    return Promise.resolve({ success: true, message: "Added Item to Cart" })
                }
            }).catch(error => {
                console.error("Failed to add item to cart:", error)
                return Promise.reject(error)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Update an item in the cart
    const updateItemInCart = async (item) => {
        setLoading(true)
        const body = {
            prykey: item.prykey,
            qty: item.qty,
            remarks: item.remarks
        }
        return RestService.post(URLS.CART_URL, body)
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setCart((prevCart) => prevCart.map(cartItem => {
                        if (String(cartItem.prykey) === String(item.prykey)) {
                            cartItem.qty = item.qty
                            cartItem.remarks = item.remarks
                            return cartItem
                        }
                        return cartItem
                    }))
                    showSuccess(data.message)
                    return Promise.resolve({ success: true, message: data.message })
                }
            }).catch(error => {
                console.error("Failed to update item in cart:", error)
                return Promise.reject(error)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Remove an item from the cart
    const removeItemFromCart = async (prykey) => {
        setLoading(true)
        return RestService.delete(URLS.CART_ID_URL.replace("{id}", prykey))
            .then(({ statusCode, data }) => {
                if (statusCode === 200 && data.success) {
                    setCart((prevCart) => prevCart.filter((item) => item.prykey !== prykey))
                    showSuccess(data.message)
                    return Promise.resolve({ success: true, message: data.message })
                } else {
                    console.error("Failed to remove item from cart", data.message)
                    return Promise.reject({ error: data.message })
                }
            }).catch(error => {
                console.error("Failed to remove item from cart:", error)
                return Promise.reject(error)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Clear the cart
    const clearCart = async () => {
        setLoading(true)
        return RestService.delete(`${URLS.CART_URL}?clientCode=${selectedSupplier.clientcode}&custCode=${selectedBranch.custcode}`)
            .then(({ statusCode, data }) => {
                if (statusCode === 200 && data.success) {
                    setCart([])
                    showSuccess(data.message)
                    return Promise.resolve({ success: true, message: data.message })
                } else {
                    console.error("Failed to clear cart:", data.message)
                    return Promise.reject({ error: data.message })
                }
            }).catch(error => {
                console.error("Failed to clear cart:", error)
                return Promise.reject(error)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const getCartItem = (prdcode) => {
        return cart.find(cartItem => cartItem.prdcode === prdcode)
    }

    // Fetch cart on component mount
    useEffect(() => {
        if (isAuthenticated && !isLoading && selectedSupplier && selectedBranch && !branchForSessionOpen && !supplierForSessionOpen) {
            fetchCart()
        }
    }, [selectedSupplier, selectedBranch, isAuthenticated, isLoading, branchForSessionOpen, supplierForSessionOpen])

    // Cart item count
    const cartItemCount = cart.length

    return (
        <CartContext.Provider
            value={{
                cart,
                addItemToCart,
                updateItemInCart,
                removeItemFromCart,
                clearCart,
                cartItemCount,
                loading,
                getCartItem,
                fetchCart,
            }}
        >
            {children}
        </CartContext.Provider>
    )
}

const useCartContext = () => {
    const {
        cart,
        addItemToCart,
        updateItemInCart,
        removeItemFromCart,
        clearCart,
        cartItemCount,
        loading,
        getCartItem,
        fetchCart,
    } = useContext(CartContext)

    return {
        cart,
        addItemToCart,
        updateItemInCart,
        removeItemFromCart,
        clearCart,
        cartItemCount,
        loading,
        getCartItem,
        fetchCart,
    }
}

export { CartProvider, useCartContext }